<template>
  <Dialog
      modal
      v-model:visible="isDialogVisible"
      header="Apply Change Order"
      @show="searchForItemsNotOnQuote()"
      @hide="resetChangeOrder()"
      style="max-width: 95%;"
      :pt="{
        closeButton: {
          onClick: () => props.closeDialog()
        }
      }"
    >
      <div class="flex items-center gap-1 mb-4">
        <DataTable
          stripedRows
          :value="applyChangeOrderStore.billablesNotOnQuote"
          tableStyle="width: 100%;"
        >
          <template #empty>
            <div v-if="fetchingBillables">
              Loading... <i class="ml-2 pi pi-spin pi-spinner"></i>
            </div>
            <div v-else>
              No items found.
            </div>
          </template>
          <template #header style="background: none; "> 
            <div v-if="applyChangeOrderStore.billablesNotOnQuote?.length">
              Found <b>{{ applyChangeOrderStore.billablesNotOnQuote.length }}</b> items not on quote.
            </div>
          </template>
          <Column
            v-if="applyChangeOrderStore.billablesNotOnQuote?.length"
            field="id"
            header=""
          >
            <template #header>
              <div v-if="fetchingMaterials">
                Loading types... <i class="ml-2 pi pi-spin pi-spinner"></i>
              </div>
            </template>
            <template #body="billableNotOnQuote">
              <Dropdown
                v-model="applyChangeOrderStore.lineItemTypeMapping[billableNotOnQuote.index].line_item_type"
                class="w-full"
                :options="lineItemTypeDropdownOptions"
                optionLabel="label"
                optionValue="value"
                :disabled="fetchingMaterials || fetchingEtcs"
              >
              </Dropdown>
            </template>
          </Column>
          <Column field="material_number" header="Material Number"></Column>
          <Column 
            field="material_long_description"
            header="Description" 
            bodyStyle="overflow-wrap: anywhere; max-width: 50em;"
          >
            <template #body="slotProps">
              <div v-if="slotProps.data.material_long_description || slotProps.data.material_description">
                {{ slotProps.data.material_long_description || slotProps.data.material_description }}
              </div>
              <div v-else>
                <i>No description</i>
              </div>

              <div v-if="slotProps.data.notes_description" class="mt-1">
                <b style="padding-right: 5px">Note:</b>{{ slotProps.data.notes_description }}
              </div>
            </template>
          </Column>
          <Column field="price" header="Cost"></Column>
          <Column field="pricing_date" header="Pricing Date"></Column>
          <Column field="margin" header="Margin"></Column>
          <Column field="quote_price" header="Unit Price"></Column>
          <Column field="quantity" header="Qty"></Column>
          <Column field="total" header="Total"></Column>
        </DataTable>
      </div>
      <div class="flex justify-end gap-2">
        <Button
          type="button"
          label="Cancel"
          severity="danger"
          @click="props.closeDialog()"
        />
        <Button
          type="button"
          label="Apply"
          severity="info"
          @click="applyChangeOrder();"
          :disabled="isApplyChangeOrderDisabled"
          v-tooltip.right="'Apply all above items to your Quote Design List.'"
        />
      </div>
    </Dialog>
</template>

<script setup lang="ts">
import {
  AllQuoteFieldsFragment as QuoteType,
  QuoteLineItemFieldsFragment as QuoteLineItemType,
  SkBillable,
  SkMaterial,
  useSkBillablesQuery,
  useSkMaterialsQuery,
  useSkEquipmentTypeConversionsQuery
} from "gql/api";
import Dialog from 'primevue/dialog';
import { computed, reactive, toRefs, onMounted, PropType } from "vue";
import { useToast } from "primevue/usetoast";
import { createQueryString, LineItemType } from "@/utils/quoteHelpers";
import { useApplyChangeOrderStore } from '../../store/apply_change_order_store';
import type { SkMaterialsQuery, SkBillablesQuery, SkEquipmentTypeConversion } from "gql/api";
import type { ComputedRef } from "vue";
import type { UseQueryResponse, UseQueryState, AnyVariables } from "@urql/vue";


// REACTIVES - REFS:
const toast = useToast();
const applyChangeOrderStore = useApplyChangeOrderStore();

const props = defineProps({
  quote: {
    type: Object as PropType<QuoteType>,
    required: true,
    default: null,
  },
  isDialogVisible: {
    type: Boolean,
    required: true,
    default: false,
  },
  openDialog: {
    type: Function,
    required: true,
    default: () => {}
  },
  closeDialog: {
    type: Function,
    required: true,
    default: () => {}
  }
})

// TOREFS:
const { quote, isDialogVisible } = toRefs(props)

/// MUTATIONS / QUERIES:
const { executeQuery: fetchBillables, fetching: fetchingBillables } =
  useSkBillablesQuery({
    pause: true,
    requestPolicy: "network-only",
    variables: computed(() => {
      // TODO: BillableSearchParams in types
      const queryParams = reactive({
        job_id: quote?.value?.sk_job_id,
        page_size: "1000"
      });

      return {
        summitQueryString: createQueryString(queryParams)
      };
    })
  })
const { executeQuery: fetchMaterials, fetching: fetchingMaterials } = useSkMaterialsQuery(
  {
    pause: true,
    requestPolicy: "network-only",
    variables: computed(() => {
      const materialQueryParams = reactive({number__in: ''})

      materialQueryParams.number__in = applyChangeOrderStore.billablesNotOnQuote
        .filter((billable: SkBillable) => {
          return !billable.prod_hierarchy
        })
        .map((billable: SkBillable) => {
          return billable.material_number
        }).join(",")

      return {
        summitQueryString: createQueryString(materialQueryParams)
      };
    })
  }
);
const { executeQuery: fetchEtcs, fetching: fetchingEtcs } = useSkEquipmentTypeConversionsQuery({
  variables: computed(() => {
    return {
      summitQueryString: ""
    };
  })
});

// REACTIVES - COMPUTEDS:

const lineItemTypeDropdownOptions = computed(() => {
  return Object.values(LineItemType).map((option: string) => {
    // Capitalize the types, add spaces, and remove extra characters
    const formattedLabel = option
      .replace(/_/g, ' ')
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1)) // Capitalize the first letter of each word
      .join(' ');

    return {
      value: option,
      label: formattedLabel
    }
  });
})

/**
 * This property determines if the 'Apply' button is enabled or disabled.
 * 
 * @returns {boolean} - True if should disable, false if should enable
 */
const isApplyChangeOrderDisabled: ComputedRef<boolean> = computed<boolean>(() => {
  return (
    fetchingBillables.value ||
    applyChangeOrderStore.billablesNotOnQuote.length === 0
  )
})

// NONREACTIVES:

/**
 * This function pings SK for all abstract Billables. Returns a Promise.
 * 
 * In this function, JavaScript will filter out the ones not in the quote's
 * current state of quote line` items.
 * 
 * @returns {Promise<void>}
 */
const searchForItemsNotOnQuote = (): Promise<void> => {
  applyChangeOrderStore.resetStore();
  return Promise.resolve()
    .then(() => fetchBillables())
    .then((resp: UseQueryState<SkBillablesQuery, AnyVariables>) => {
      if (resp?.error?.value) {
        return Promise.reject(new Error(`${resp.error.value}`));
      }

      // Note: current spec doesn't require all billables found, but it might later
      applyChangeOrderStore.billables = (resp?.data?.value?.getSKBillables || [])
        .filter((billable): billable is SkBillable => billable !== null);
      const foundBillables: Boolean = applyChangeOrderStore.billables?.length > 0;

      if (foundBillables) {
        // Extract all Material Numbers currently on quote to compare against:
        const allQuoteLineItemMns: Array<string | null | undefined> = quote?.value?.quote_line_items
          .map((qli: QuoteLineItemType) => qli.erp_material_number)
          .filter(Boolean)

        // The important part, actually assign billables *not* found on quote:
        applyChangeOrderStore.billablesNotOnQuote = applyChangeOrderStore.billables
          .filter((billable: SkBillable) =>
            billable.material_number && !allQuoteLineItemMns.includes(billable.material_number)
          )

        applyChangeOrderStore.setLineItemTypeMapping();
      }
    })
    .catch((error) => {
      console.error(`Problem fetching billables: ${error}`);
      toast.add({
        severity: "error",
        summary: "Error",
        detail: "Could not get billables from SK.\nTry again or contact support.",
        life: 7000
      });
      // TODO: sentry report
    })
    .then((): void | PromiseLike<void> | UseQueryResponse<SkMaterialsQuery, AnyVariables> => {
      // TODO: this .then() can be eliminated if prod_hierarchy is
      // a supported field on SK's abstract Billable model.
      return applyChangeOrderStore.isBillablesMissingProductHierarchies 
        ? fetchMaterials() as UseQueryResponse<SkMaterialsQuery, AnyVariables>
        : Promise.resolve();
    })
    .then((resp: void | UseQueryResponse<SkMaterialsQuery, AnyVariables>) => {
      // TODO: this .then() can be eliminated if prod_hierarchy is
      // a supported field on SK's abstract Billable model.
      if (resp?.error?.value) {
        return Promise.reject(new Error(`${resp.error.value}`));
      }

      const materials: SkMaterial[] = (resp?.data?.value?.getSKMaterials || [])
        .filter((material): material is SkMaterial => material !== null);
      if (materials?.length) {
        applyChangeOrderStore.setMissingProductHierarchies(materials);
      }
    })
    .catch((error) => {
      console.error(`Problem getting product hierarchies: ${error}`)
      toast.add({
        severity: "warn",
        summary: "Could not automatically determine line item types",
        detail: "Manually designate your line item types.",
        life: 7000
      });
      // TODO: sentry report
    })
}

/**
 * This function applies the minimum required actions to trigger after the 'Apply'
 * button is clicked. The minimum requirements can be expanded if needed.
 * 
 * Note: for specific behavior, you can write actions in the
 * props.closeDialog enclosure on the respective component.
 * See QuoteVersionDropDown.vue for an example.
 * 
 * @returns {void}
 */
const applyChangeOrder = (): void => {
  props.closeDialog();
  applyChangeOrderStore.isApplyingChangeOrder = true;

  toast.add({
    severity: "info",
    summary: "Applying Change Order",
    detail: "Please wait while the app applies your items.",
    life: 7000
  });
}

/**
 * This function resets values to revert the dialog's state. Intended for the next
 * for the next subsequent open (regardless if 'Cancel' or 'Apply' is clicked).
 * 
 * @returns {void}
 */
const resetChangeOrder = (): void => {
  props.closeDialog();
}

onMounted(() => {
  Promise.resolve()
    .then(() => fetchEtcs())
    .then((resp) => {
      applyChangeOrderStore.equipmentTypeConversions = (resp?.data?.value?.getSKEquipmentTypeConversions || [])
        .filter((etc): etc is SkEquipmentTypeConversion => etc !== null);
    })
})
</script>

<style lang="scss" scoped>
:deep(.p-datatable-header) {
  padding: 0em 0 1em 0em;
  background: none;
  border: none;
  font-weight: unset;
}
</style>
